import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Table, Badge, Button, Spinner } from 'reactstrap';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs } from '../../../AbstractElements';
import { getDashboard, describeError, formatInr } from './purchasesApi';

const StatCard = ({ label, value, sub }) => (
  <Col md="3" sm="6">
    <Card className="h-100">
      <CardBody>
        <p className="mb-1 text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: '.08em' }}>{label}</p>
        <h4 className="mb-0">{value}</h4>
        {sub ? <small className="text-muted">{sub}</small> : null}
      </CardBody>
    </Card>
  </Col>
);

const PurchasesDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getDashboard()
      .then(setData)
      .catch((e) => setError(describeError(e, 'Could not load the purchases dashboard.')))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center p-5"><Spinner /></div>;
  if (error) {
    return (
      <Fragment>
        <Breadcrumbs parent="Purchases" title="Cost Dashboard" mainTitle="Cost Dashboard" />
        <Container fluid><Card><CardBody className="text-center p-5">
          <p className="text-danger mb-3">{error}</p>
          <Button color="secondary" size="sm" onClick={() => window.location.reload()}>Try again</Button>
        </CardBody></Card></Container>
      </Fragment>
    );
  }

  const { totals, timeline, topRisers, topFallers, averageChangePct, trendedCount } = data;

  // Costs are recorded on irregular dates — an 11-month gap sits between PI 63 and
  // PI 70 — so a datetime axis is the only honest one; a category axis would draw
  // that gap the same width as two days.
  const timelineSeries = [{
    name: 'Average cost',
    data: timeline.map((t) => [new Date(t.pi_date).getTime(), Number(t.avg_cost || 0)]),
  }];
  const timelineOptions = {
    chart: { type: 'line', height: 320, toolbar: { show: false } },
    stroke: { curve: 'straight', width: 3 },
    colors: ['#8a5a44'],
    markers: { size: 4 },
    xaxis: { type: 'datetime', labels: { datetimeUTC: false } },
    yaxis: { labels: { formatter: (v) => `₹${Math.round(v)}` } },
    dataLabels: { enabled: false },
    grid: { borderColor: '#eee' },
    tooltip: {
      x: { format: 'dd MMM yyyy' },
      y: { formatter: (v) => formatInr(v) },
    },
  };

  const volumeSeries = [{ name: 'Lines', data: timeline.map((t) => Number(t.line_count || 0)) }];
  const volumeOptions = {
    chart: { type: 'bar', height: 320, toolbar: { show: false } },
    colors: ['#b08968'],
    plotOptions: { bar: { borderRadius: 2, columnWidth: '60%' } },
    dataLabels: { enabled: false },
    xaxis: { categories: timeline.map((t) => t.pi_ref), labels: { rotate: -60, style: { fontSize: '10px' } } },
    grid: { borderColor: '#eee' },
  };

  const MoversTable = ({ title, rows, positive }) => (
    <Col xl="6">
      <Card className="h-100">
        <CardHeader className="py-3"><h5 className="mb-0">{title}</h5></CardHeader>
        <CardBody className="pt-0">
          <div className="table-responsive">
            <Table className="table-bordered mb-0" size="sm">
              <thead>
                <tr><th>Product</th><th>Size</th><th className="text-end">First</th><th className="text-end">Latest</th><th className="text-end">Change</th></tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr><td colSpan="5" className="text-center text-muted p-3">Nothing yet</td></tr>
                ) : rows.map((r, i) => (
                  <tr key={i} style={{ cursor: 'pointer' }} onClick={() => navigate(`/purchases/costs?sku=${encodeURIComponent(r.style_sku)}`)}>
                    <td>{r.description || r.style_sku}</td>
                    <td><small className="text-muted">{r.size_sku}</small></td>
                    <td className="text-end">{formatInr(r.first_cost)}</td>
                    <td className="text-end">{formatInr(r.latest_cost)}</td>
                    <td className="text-end">
                      <Badge color={positive ? 'danger' : 'success'} pill>
                        {r.change_pct > 0 ? '+' : ''}{r.change_pct}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  return (
    <Fragment>
      <Breadcrumbs parent="Purchases" title="Cost Dashboard" mainTitle="Cost Dashboard" />
      <Container fluid>
        <Row className="g-3 mb-1">
          <StatCard label="Purchase invoices" value={totals.pi_count} sub={`latest ${totals.latest_pi_ref || '—'}`} />
          <StatCard label="Styles costed" value={totals.style_count} sub={`${totals.unmatched_styles} not in catalogue`} />
          <StatCard label="Average cost change" value={`${averageChangePct > 0 ? '+' : ''}${averageChangePct}%`} sub={`across ${trendedCount} items with a trend`} />
          <StatCard label="Latest PI" value={totals.latest_pi_date || '—'} sub={`${totals.line_count} cost lines total`} />
        </Row>

        <Row className="g-3 mt-1">
          <Col xl="8">
            <Card className="h-100">
              <CardHeader className="py-3"><h5 className="mb-0">Average cost per PI over time</h5></CardHeader>
              <CardBody><Chart options={timelineOptions} series={timelineSeries} type="line" height={320} /></CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card className="h-100">
              <CardHeader className="py-3"><h5 className="mb-0">Lines per PI</h5></CardHeader>
              <CardBody><Chart options={volumeOptions} series={volumeSeries} type="bar" height={320} /></CardBody>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-1">
          <MoversTable title="Biggest cost increases" rows={topRisers} positive />
          <MoversTable title="Cost decreases" rows={topFallers} positive={false} />
        </Row>
      </Container>
    </Fragment>
  );
};

export default PurchasesDashboard;
